import React, { useState, useEffect } from 'react';
import { getUserDetails, updateUserDetails, getGamesList } from '../lib/api/userApi';

const ProfilePage = () => {
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    preferredName: '',
    gender: 0,
    age: 0,
    region: '',
    gameId: 0,
    experience: 0,
    otherGame: ''
  });
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch user details and games on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch user details
        const { data: userDetailsData, error: userDetailsError } = await getUserDetails();
        if (userDetailsError) throw userDetailsError;
        
        // Fetch games list
        const { data: gamesData, error: gamesError } = await getGamesList();
        if (gamesError) throw gamesError;
        
        // Set data
        setUserData(userDetailsData);
        setGames(gamesData);
        
        // Set form data from user details
        setFormData({
          firstName: userDetailsData.firstName || '',
          lastName: userDetailsData.lastName || '',
          preferredName: userDetailsData.preferredName || '',
          gender: userDetailsData.gender || 0,
          age: userDetailsData.age || 0,
          region: userDetailsData.region || '',
          gameId: userDetailsData.gameId || 0,
          experience: userDetailsData.experience || 0,
          otherGame: userDetailsData.otherGame || ''
        });
      } catch (err) {
        console.error('Error fetching profile data:', err);
        setError('Failed to load your profile information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'age' ? parseInt(value, 10) || 0 : value
    });
  };

  // Handle select change
  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const { data, error: updateError } = await updateUserDetails(formData);
      
      if (updateError) throw updateError;
      
      setUserData(data);
      setSuccess('Profile updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update your profile. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  // Gender options
  const genderOptions = [
    { id: 0, text: 'Prefer not to say' },
    { id: 1, text: 'Male' },
    { id: 2, text: 'Female' },
    { id: 3, text: 'Other' }
  ];

  // Experience level options
  const experienceOptions = [
    { id: 0, text: 'Beginner' },
    { id: 1, text: 'Intermediate' },
    { id: 2, text: 'Advanced' },
    { id: 3, text: 'Professional' }
  ];

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="max-w-3xl mx-auto p-6">
        <h1 className="text-3xl font-bold mb-6">Your Profile</h1>
        
        {error && (
          <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-lg">
            {success}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="profile-page__form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* First Name */}
            <div className="profile-page__form-field">
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your first name"
              />
            </div>
            
            {/* Last Name */}
            <div className="profile-page__form-field">
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your last name"
              />
            </div>
            
            {/* Preferred Name */}
            <div className="profile-page__form-field">
              <label htmlFor="preferredName" className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Name
              </label>
              <input
                type="text"
                id="preferredName"
                name="preferredName"
                value={formData.preferredName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="What should we call you?"
              />
            </div>
            
            {/* Age */}
            <div className="profile-page__form-field">
              <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-2">
                Age
              </label>
              <input
                type="number"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                min="8"
                max="120"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your age"
              />
            </div>
            
            {/* Region */}
            <div className="profile-page__form-field">
              <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-2">
                Region
              </label>
              <input
                type="text"
                id="region"
                name="region"
                value={formData.region}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your region or country"
              />
            </div>
          </div>
          
          {/* Gender Selection */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Gender</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {genderOptions.map((option) => (
                <div 
                  key={option.id}
                  onClick={() => handleSelectChange('gender', option.id)}
                  className={`p-4 rounded-lg cursor-pointer transition-all ${
                    formData.gender === option.id
                      ? 'bg-blue-50 shadow-[0_0_10px_rgba(59,130,246,0.5)]' 
                      : 'border hover:border-blue-400 hover:shadow-sm'
                  }`}
                >
                  <h4 className="text-sm font-medium text-gray-800 text-center">{option.text}</h4>
                </div>
              ))}
            </div>
          </div>
          
          {/* Game Selection */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Your Game</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
              {games.map((game) => (
                <div 
                  key={game.gameId}
                  onClick={() => handleSelectChange('gameId', game.gameId)}
                  className={`p-4 rounded-lg cursor-pointer transition-all ${
                    formData.gameId === game.gameId
                      ? 'bg-blue-50 shadow-[0_0_10px_rgba(59,130,246,0.5)]' 
                      : 'border hover:border-blue-400 hover:shadow-sm'
                  }`}
                >
                  <h4 className="text-sm font-medium text-gray-800 text-center">{game.gameName}</h4>
                </div>
              ))}
              
              {/* Other option */}
              <div 
                onClick={() => handleSelectChange('gameId', 999)}
                className={`p-4 rounded-lg cursor-pointer transition-all ${
                  formData.gameId === 999
                    ? 'bg-blue-50 shadow-[0_0_10px_rgba(59,130,246,0.5)]' 
                    : 'border hover:border-blue-400 hover:shadow-sm'
                }`}
              >
                <h4 className="text-sm font-medium text-gray-800 text-center">Other</h4>
              </div>
            </div>
            
            {/* Custom game input */}
            {formData.gameId === 999 && (
              <input
                type="text"
                name="otherGame"
                value={formData.otherGame}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your game/sport"
              />
            )}
          </div>
          
          {/* Experience Level */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Experience Level</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {experienceOptions.map((option) => (
                <div 
                  key={option.id}
                  onClick={() => handleSelectChange('experience', option.id)}
                  className={`p-4 rounded-lg cursor-pointer transition-all ${
                    formData.experience === option.id
                      ? 'bg-blue-50 shadow-[0_0_10px_rgba(59,130,246,0.5)]' 
                      : 'border hover:border-blue-400 hover:shadow-sm'
                  }`}
                >
                  <h4 className="text-sm font-medium text-gray-800 text-center">{option.text}</h4>
                </div>
              ))}
            </div>
          </div>
          
          {/* Submit Button */}
          <div className="profile-page__form-actions mt-8">
            <button
              type="submit"
              disabled={updating}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updating ? 'Updating...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage; 